import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as vendingService from "data/server_apis/vendingServices";
import * as accountService from "data/server_apis/accountService";
import { unionBy } from "lodash";
import {
  machinePhotoBuilder,
  machinePhotosState,
} from "store/reducers/machine_func/machinePhotos";
import {
  machineAssignmentBuilder,
  machineAssignmentState,
} from "store/reducers/machine_func/machineAssignments";
import {
  machineAssignmentToLocation,
  machineAssignmentToLocationBuilder,
  machineAssignmentToLocationState,
} from "store/reducers/machine_func/machineAssignmentToLocation";
import { createPaymentAssignmentThunk } from "./payments";

const initialState = {
  // Machine
  createMachineLoading: false,
  createMachineError: false,
  createMachineErrorMessage: null,
  createMachineSuccess: false,
  currentMachine: null,

  getMachinesLoading: false,
  getMachinesError: false,
  getMachinesErrorMessage: null,
  getMachinesSuccess: false,

  getMinMachinesLoading: false,
  getMinMachinesError: false,
  getMinMachinesErrorMessage: null,
  getMinMachinesSuccess: false,
  minMachines: [],

  getAllMachinesLoading: false,
  getAllMachinesError: false,
  getAllMachinesErrorMessage: null,
  getAllMachinesSuccess: false,

  getMachineLoading: false,
  getMachineError: false,
  getMachineErrorMessage: null,
  getMachineSuccess: false,

  updateMachineLoading: false,
  updateMachineError: false,
  updateMachineErrorMessage: null,
  updateMachineSuccess: false,

  deleteMachineLoading: {},
  deleteMachineError: {},
  deleteMachineErrorMessage: {},
  deleteMachineSuccess: {},

  activateMachineLoading: {},
  activateMachineError: {},
  activateMachineErrorMessage: {},
  activateMachineSuccess: {},

  // Machine user

  loadingMachineUserCreate: false,
  errorMachineUserCreate: false,
  successMachineUserCreate: false,
  errorMessageMachineUserCreate: null,
  createdMachineUser: null,

  // Cabin
  createCabinLoading: false,
  createCabinError: false,
  createCabinErrorMessage: null,
  createCabinSuccess: false,

  createMultipleCabinLoading: false,
  createMultipleCabinError: false,
  createMultipleCabinErrorMessage: null,
  createMultipleCabinSuccess: false,

  getCabinsLoading: false,
  getCabinsError: false,
  getCabinsErrorMessage: null,
  getCabinsSuccess: false,

  getAllCabinsLoading: false,
  getAllCabinsError: false,
  getAllCabinsErrorMessage: null,
  getAllCabinsSuccess: false,

  getCabinLoading: false,
  getCabinError: false,
  getCabinErrorMessage: null,
  getCabinSuccess: false,

  updateCabinLoading: false,
  updateCabinError: false,
  updateCabinErrorMessage: null,
  updateCabinSuccess: false,

  deleteCabinLoading: false,
  deleteCabinError: false,
  deleteCabinErrorMessage: null,
  deleteCabinSuccess: false,

  activateCabinLoading: false,
  activateCabinError: false,
  activateCabinErrorMessage: null,
  activateCabinSuccess: false,

  // Tray
  createTrayLoading: false,
  createTrayError: false,
  createTrayErrorMessage: null,
  createTraySuccess: false,

  createMultyTrayLoading: {},
  createMultyTrayError: {},
  createMultyTrayErrorMessage: {},
  createMultyTraySuccess: {},

  getTraysLoading: false,
  getTraysError: false,
  getTraysErrorMessage: null,
  getTraysSuccess: false,

  getAllTraysLoading: false,
  getAllTraysError: false,
  getAllTraysErrorMessage: null,
  getAllTraysSuccess: false,

  getTrayLoading: false,
  getTrayError: false,
  getTrayErrorMessage: null,
  getTraySuccess: false,

  updateTrayLoading: false,
  updateTrayError: false,
  updateTrayErrorMessage: null,
  updateTraySuccess: false,

  deleteTrayLoading: {},
  deleteTrayError: {},
  deleteTrayErrorMessage: {},
  deleteTraySuccess: {},

  activateTrayLoading: false,
  activateTrayError: false,
  activateTrayErrorMessage: null,
  activateTraySuccess: false,

  getTrayByCabinLoading: false,
  getTrayByCabinError: false,
  getTrayByCabinErrorMessage: null,
  getTrayByCabinSuccess: false,

  // Partition
  createPartitionLoading: false,
  createPartitionError: false,
  createPartitionErrorMessage: null,
  createPartitionSuccess: false,

  updatePartitionLoading: false,
  updatePartitionError: false,
  updatePartitionErrorMessage: null,
  updatePartitionSuccess: false,

  createMultiplePartitionLoading: {},
  createMultiplePartitionError: {},
  createMultiplePartitionErrorMessage: {},
  createMultiplePartitionSuccess: {},

  getPartitionsLoading: false,
  getPartitionsError: false,
  getPartitionsErrorMessage: null,
  getPartitionsSuccess: false,

  getPartitionsByTrayLoading: false,
  getPartitionsByTrayError: false,
  getPartitionsByTrayErrorMessage: null,
  getPartitionsByTraySuccess: false,

  getAllPartitionsLoading: false,
  getAllPartitionsError: false,
  getAllPartitionsErrorMessage: null,
  getAllPartitionsSuccess: false,

  getPartitionLoading: false,
  getPartitionError: false,
  getPartitionErrorMessage: null,
  getPartitionSuccess: false,

  updatePartitionLoading: false,
  updatePartitionError: false,
  updatePartitionErrorMessage: null,
  updatePartitionSuccess: false,

  deletePartitionLoading: {},
  deletePartitionError: {},
  deletePartitionErrorMessage: {},
  deletePartitionSuccess: {},

  activatePartitionLoading: false,
  activatePartitionError: false,
  activatePartitionErrorMessage: null,
  activatePartitionSuccess: false,
  machinePageCount: 1,
  machines: [],
  cabins: {},
  trays: {},
  partitions: {},
  ...machinePhotosState,
  ...machineAssignmentState,
  ...machineAssignmentToLocationState,
};

// Machine

export const createMachineThunk = createAsyncThunk(
  "vending/createMachine",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createMachine(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getMachinesThunk = createAsyncThunk(
  "vending/getMachines",
  async ({ page, location }, { rejectWithValue }) => {
    try {
      return (await vendingService.getMachines(page, location)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
export const getMinMachinesThunk = createAsyncThunk(
  "vending/getMinMachines",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getMinMachines()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getAllMachinesThunk = createAsyncThunk(
  "vending/getAllMachines",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getAllMachines()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getMachineThunk = createAsyncThunk(
  "vending/getMachine",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updateMachineThunk = createAsyncThunk(
  "vending/updateMachine",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await vendingService.updateMachine(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteMachineThunk = createAsyncThunk(
  "vending/deleteMachine",
  async (id, { rejectWithValue }) => {
    try {
      // console.log("In Error");
      return (await vendingService.deleteMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
export const activateMachineThunk = createAsyncThunk(
  "vending/activateMachine",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.activateMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

// Machine user

export const createMachineUserThunk = createAsyncThunk(
  "vending/createMachineUser",
  async (data, { rejectWithValue }) => {
    try {
      return (await accountService.createVendingMachine(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

// Cabin

export const createCabinThunk = createAsyncThunk(
  "vending/createCabin",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createCabin(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getCabinsThunk = createAsyncThunk(
  "vending/getCabins",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getCabins()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getAllCabinsThunk = createAsyncThunk(
  "vending/getAllCabins",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getAllCabins()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getCabinThunk = createAsyncThunk(
  "vending/getCabin",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getCabin(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updateCabinThunk = createAsyncThunk(
  "vending/updateCabin",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await vendingService.updateCabin(id, data)).data;
    } catch (err) {
      console.log("Error", err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteCabinThunk = createAsyncThunk(
  "vending/deleteCabin",
  async ({ id, m_id }, { rejectWithValue }) => {
    try {
      return (await vendingService.deleteCabin(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
export const activateCabinThunk = createAsyncThunk(
  "vending/activateCabin",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.activateMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const createMultipleCabinsThunk = createAsyncThunk(
  "vending/createMultipleCabins",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createMultipleCabins(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getCabinsByMachineThunk = createAsyncThunk(
  "vending/getCabinsByMachine",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getCabinsByMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

// Tray

export const createTrayThunk = createAsyncThunk(
  "vending/createTray",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createTray(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const createMultyTrayThunk = createAsyncThunk(
  "vending/createMultyTray",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createMultipleTray(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getTraysThunk = createAsyncThunk(
  "vending/getTrays",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getTrays()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getAllTraysThunk = createAsyncThunk(
  "vending/getAllTrays",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getAllTrays()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getTrayThunk = createAsyncThunk(
  "vending/getTray",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getTray(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updateTrayThunk = createAsyncThunk(
  "vending/updateTray",
  async (id, data, { rejectWithValue }) => {
    try {
      return (await vendingService.updateTray(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteTrayThunk = createAsyncThunk(
  "vending/deleteTray",
  async ({ id, cabin_id }, { rejectWithValue }) => {
    try {
      return (await vendingService.deleteTray(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
export const activateTrayThunk = createAsyncThunk(
  "vending/activateTray",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.activateMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
export const getTraysByCabinThunk = createAsyncThunk(
  "vending/getTraysByCabin",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getTrayByCabin(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

// Partition

export const createPartitionThunk = createAsyncThunk(
  "vending/createPartition",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createPartition(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const createMultiplePartitionThunk = createAsyncThunk(
  "vending/createMultiplePartition",
  async (data, { rejectWithValue }) => {
    try {
      return (await vendingService.createMultyPartition(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getPartitionsThunk = createAsyncThunk(
  "vending/getPartitions",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getPartitions()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getAllPartitionsThunk = createAsyncThunk(
  "vending/getAllPartitions",
  async (_, { rejectWithValue }) => {
    try {
      return (await vendingService.getAllPartitions()).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getPartitionThunk = createAsyncThunk(
  "vending/getPartition",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getPartition(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updatePartitionThunk = createAsyncThunk(
  "vending/updatePartition",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await vendingService.updatePartition(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deletePartitionThunk = createAsyncThunk(
  "vending/deletePartition",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.deletePartition(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
export const activatePartitionThunk = createAsyncThunk(
  "vending/activatePartition",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.activateMachine(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getPartitionsByTrayThunk = createAsyncThunk(
  "vending/getPartitionsByTray",
  async (id, { rejectWithValue }) => {
    try {
      return (await vendingService.getPartitionsByTray(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

const vending = createSlice({
  name: "vending",
  initialState,
  reducers: {
    clearMachineState(state, action) {
      state.createMachineError = false;
      state.createMachineErrorMessage = null;
      state.createMachineSuccess = false;
      state.currentMachine = null;

      state.updateMachineError = false;
      state.updateMachineErrorMessage = null;
      state.updateMachineSuccess = false;

      state.deleteMachineLoading = {};
      state.deleteMachineError = {};
      state.deleteMachineErrorMessage = {};
      state.deleteMachineSuccess = {};

      state.activateMachineLoading = {};
      state.activateMachineError = {};
      state.activateMachineErrorMessage = {};
      state.activateMachineSuccess = {};

      state.createMultipleCabinLoading = false;
      state.createMultipleCabinError = false;
      state.createMultipleCabinSuccess = false;
      state.createMultipleCabinErrorMessage = null;

      state.createMultyTrayLoading = {};
      state.createMultyTrayError = {};
      state.createMultyTraySuccess = {};
      state.createMultyTrayErrorMessage = {};
    },
    clearMachineUser(state, action) {
      state.loadingMachineUserCreate = false;
      state.errorMachineUserCreate = false;
      state.successMachineUserCreate = false;
      state.errorMessageMachineUserCreate = null;
      state.createdMachineUser = null;
    },
    clearCabinState(state) {
      state.createCabinLoading = false;
      state.createCabinError = false;
      state.createCabinErrorMessage = null;
      state.createCabinSuccess = false;
      state.updateCabinLoading = false;
      state.updateCabinError = false;
      state.updateCabinErrorMessage = null;
      state.updateCabinSuccess = false;
    },
    clearTrayState(state, action) {
      state.getTrayByCabinLoading = false;
      state.getTrayByCabinError = false;
      state.getTrayByCabinErrorMessage = null;
      state.getTrayByCabinSuccess = false;

      state.createTrayLoading = false;
      state.createTrayError = false;
      state.createTrayErrorMessage = null;
      state.createTraySuccess = false;
    },
    clearPartitionState(state, action) {
      state.getPartitionByTrayLoading = false;
      state.getPartitionByTrayError = false;
      state.getPartitionByTrayErrorMessage = null;
      state.getPartitionByTraySuccess = false;

      state.createPartitionLoading = false;
      state.createPartitionError = false;
      state.createPartitionErrorMessage = null;
      state.createPartitionSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // Machine
    builder
      .addCase(createMachineThunk.pending, (state, action) => {
        state.createMachineLoading = true;
        state.createMachineError = false;
        state.createMachineErrorMessage = null;
        state.createMachineSuccess = false;
      })
      .addCase(createMachineThunk.rejected, (state, action) => {
        state.createMachineLoading = false;
        state.createMachineError = true;
        state.createMachineErrorMessage = action.payload?.message;
        state.createMachineSuccess = false;
      })
      .addCase(createMachineThunk.fulfilled, (state, action) => {
        state.createMachineLoading = false;
        state.createMachineError = false;
        state.createMachineErrorMessage = null;
        state.createMachineSuccess = true;
        const machine = action.payload;
        state.machines.push(machine);
        state.currentMachine = machine;
      });

    builder.addCase(createPaymentAssignmentThunk.fulfilled, (state, action) => {
      state.machines = state.machines.map((machine) => {
        if (machine.id === action.payload.machine) {
          return {
            ...machine,
            payments: [...machine.payments, action.meta.arg.paymentFull],
          };
        }
        return machine;
      })
    })

    builder
      .addCase(getMachinesThunk.pending, (state, action) => {
        state.getMachinesLoading = true;
        state.getMachinesError = false;
        state.getMachinesErrorMessage = null;
        state.getMachinesSuccess = false;
      })
      .addCase(getMachinesThunk.rejected, (state, action) => {
        state.getMachinesLoading = false;
        state.getMachinesError = true;
        state.getMachinesErrorMessage = action.payload?.message;
        state.getMachinesSuccess = false;
      })
      .addCase(getMachinesThunk.fulfilled, (state, action) => {
        state.getMachinesLoading = false;
        state.getMachinesError = false;
        state.getMachinesErrorMessage = null;
        state.getMachinesSuccess = false;
        if (Array.isArray(action.payload)) {
          state.machines = action.payload;
        } else {
          state.machines = action.payload.results;
        }

        state.machinePageCount = action.payload.total_pages;
      });

    builder
      .addCase(getAllMachinesThunk.pending, (state, action) => {
        state.getAllMachinesLoading = true;
        state.getAllMachinesError = false;
        state.getAllMachinesErrorMessage = null;
        state.getAllMachinesSuccess = false;
      })
      .addCase(getAllMachinesThunk.rejected, (state, action) => {
        state.getAllMachinesLoading = false;
        state.getAllMachinesError = true;
        state.getAllMachinesErrorMessage = action.payload?.message;
        state.getAllMachinesSuccess = false;
      })
      .addCase(getAllMachinesThunk.fulfilled, (state, action) => {
        state.getAllMachinesLoading = false;
        state.getAllMachinesError = false;
        state.getAllMachinesErrorMessage = null;
        state.getAllMachinesSuccess = false;
        state.machines = action.payload;
      });

    builder
      .addCase(getMachineThunk.pending, (state, action) => {
        state.getMachineLoading = true;
        state.getMachineError = false;
        state.getMachineErrorMessage = null;
        state.getMachineSuccess = false;
      })
      .addCase(getMachineThunk.rejected, (state, action) => {
        state.getMachineLoading = false;
        state.getMachineError = true;
        state.getMachineErrorMessage = action.payload?.message;
        state.getMachineSuccess = false;
      })
      .addCase(getMachineThunk.fulfilled, (state, action) => {
        state.getMachineLoading = false;
        state.getMachineError = false;
        state.getMachineErrorMessage = null;
        state.getMachineSuccess = false;
        const index = state.machines.findIndex(
          (items) => items.id === action.payload.id
        );
        if (index > -1) {
          state.machines[index] = {
            ...state.machines[index],
            ...action.payload,
          };
        } else {
          state.machines.push(action.payload);
        }
      });

    builder
      .addCase(getMinMachinesThunk.pending, (state, action) => {
        state.getMinMachinesLoading = true;
        state.getMinMachinesError = false;
        state.getMinMachinesErrorMessage = null;
        state.getMinMachinesSuccess = false;
      })
      .addCase(getMinMachinesThunk.rejected, (state, action) => {
        state.getMinMachinesLoading = false;
        state.getMinMachinesError = true;
        state.getMinMachinesErrorMessage = action.payload?.message;
        state.getMinMachinesSuccess = false;
      })
      .addCase(getMinMachinesThunk.fulfilled, (state, action) => {
        state.getMinMachinesLoading = false;
        state.getMinMachinesError = false;
        state.getMinMachinesErrorMessage = null;
        state.getMinMachinesSuccess = false;
        state.minMachines = action.payload;
      });

    builder
      .addCase(updateMachineThunk.pending, (state, action) => {
        state.updateMachineLoading = true;
        state.updateMachineError = false;
        state.updateMachineErrorMessage = null;
        state.updateMachineSuccess = false;
      })
      .addCase(updateMachineThunk.rejected, (state, action) => {
        console.log("action", action);
        state.updateMachineLoading = false;
        state.updateMachineError = true;
        state.updateMachineErrorMessage = action.payload?.message;
        state.updateMachineSuccess = false;
      })
      .addCase(updateMachineThunk.fulfilled, (state, action) => {
        state.updateMachineLoading = false;
        state.updateMachineError = false;
        state.updateMachineErrorMessage = null;
        state.updateMachineSuccess = true;
        state.currentMachine = action.payload;
        const index = state.machines.findIndex(
          (items) => items.id === action.payload.id
        );
        if (index > -1) {
          state.machines[index] = {
            ...state.machines[index],
            ...action.payload,
          };
        } else {
          state.machines.push(action.payload.id);
        }
      });

    builder
      .addCase(deleteMachineThunk.pending, (state, action) => {
        state.deleteMachineLoading[action.meta.arg] = true;
        state.deleteMachineError[action.meta.arg] = false;
        state.deleteMachineErrorMessage[action.meta.arg] = null;
        state.deleteMachineSuccess[action.meta.arg] = false;
      })
      .addCase(deleteMachineThunk.rejected, (state, action) => {
        state.deleteMachineLoading[action.meta.arg] = false;
        state.deleteMachineError[action.meta.arg] = true;
        state.deleteMachineErrorMessage[action.meta.arg] =
          action.payload?.message;
        state.deleteMachineSuccess[action.meta.arg] = false;
      })
      .addCase(deleteMachineThunk.fulfilled, (state, action) => {
        state.deleteMachineLoading[action.meta.arg] = false;
        state.deleteMachineError[action.meta.arg] = false;
        state.deleteMachineErrorMessage[action.meta.arg] = null;
        state.deleteMachineSuccess[action.meta.arg] = false;
        const machine = state.machines.find(
          (items) => items.id === action.meta.arg
        );
        if (machine) {
          machine.active = false;
        }
      });

    builder
      .addCase(activateMachineThunk.pending, (state, action) => {
        state.activateMachineLoading[action.meta.arg] = true;
        state.activateMachineError[action.meta.arg] = false;
        state.activateMachineErrorMessage[action.meta.arg] = null;
        state.activateMachineSuccess[action.meta.arg] = false;
      })
      .addCase(activateMachineThunk.rejected, (state, action) => {
        state.activateMachineLoading[action.meta.arg] = false;
        state.activateMachineError[action.meta.arg] = true;
        state.activateMachineErrorMessage[action.meta.arg] =
          action.payload?.message;
        state.activateMachineSuccess[action.meta.arg] = false;
      })
      .addCase(activateMachineThunk.fulfilled, (state, action) => {
        state.activateMachineLoading[action.meta.arg] = false;
        state.activateMachineError[action.meta.arg] = false;
        state.activateMachineErrorMessage[action.meta.arg] = null;
        state.activateMachineSuccess[action.meta.arg] = false;
        const machine = state.machines.find(
          (items) => items.id === action.meta.arg
        );
        if (machine) {
          machine.active = true;
        }
      });

    builder
      .addCase(createMachineUserThunk.pending, (state, action) => {
        state.loadingMachineUserCreate = true;
        state.errorMachineUserCreate = false;
        state.successMachineUserCreate = false;
        state.errorMessageMachineUserCreate = null;
        state.createdMachineUser = null;
      })
      .addCase(createMachineUserThunk.rejected, (state, action) => {
        state.loadingMachineUserCreate = false;
        state.errorMachineUserCreate = true;
        state.successMachineUserCreate = false;
        state.errorMessageMachineUserCreate = action.payload?.message;
        state.createdMachineUser = null;
      })
      .addCase(createMachineUserThunk.fulfilled, (state, action) => {
        state.loadingMachineUserCreate = false;
        state.errorMachineUserCreate = false;
        state.successMachineUserCreate = true;
        state.errorMessageMachineUserCreate = null;
        state.createdMachineUser = action.payload;
      });

    // Cabins
    builder
      .addCase(createMultipleCabinsThunk.pending, (state, action) => {
        state.createMultipleCabinLoading = true;
        state.createMultipleCabinError = false;
        state.createMultipleCabinSuccess = false;
        state.createMultipleCabinErrorMessage = null;
      })
      .addCase(createMultipleCabinsThunk.rejected, (state, action) => {
        state.createMultipleCabinLoading = false;
        state.createMultipleCabinError = true;
        state.createMultipleCabinSuccess = false;
        state.createMultipleCabinErrorMessage = action.payload?.message;
      })
      .addCase(createMultipleCabinsThunk.fulfilled, (state, action) => {
        console.log("action", action);
        if (Boolean(state.currentMachine)) {
          state.currentMachine.cabin_count = action.payload.length;
        }
        const machine = state.machines.find(
          (item) => item.id === action.payload[0].machine
        );
        if (machine) {
          machine.cabin_count += action.payload.length;
        }
        if (action.payload.length > 0) {
          const machine = action.payload[0].machine;
          if (machine in state.cabins) {
            const cabins = [...action.payload, state.cabins[machine]];
            const unionCabins = unionBy(cabins, "id");
            state.cabins[machine] = unionCabins;
          } else {
            state.cabins[machine] = action.payload;
          }
        }
        state.createMultipleCabinLoading = false;
        state.createMultipleCabinError = false;
        state.createMultipleCabinSuccess = true;
        state.createMultipleCabinErrorMessage = null;
      });

    builder
      .addCase(getCabinsByMachineThunk.pending, (state, action) => {
        state.getCabinsLoading = true;
        state.getCabinsError = false;
        state.getCabinsSuccess = false;
        state.getCabinsErrorMessage = null;
      })
      .addCase(getCabinsByMachineThunk.rejected, (state, action) => {
        state.getCabinsLoading = false;
        state.getCabinsError = true;
        state.getCabinsSuccess = false;
        state.getCabinsErrorMessage = action.payload?.message;
      })
      .addCase(getCabinsByMachineThunk.fulfilled, (state, action) => {
        if (Boolean(state.currentMachine)) {
          state.currentMachine.cabin_count = action.payload.length;
        }
        state.cabins[action.meta.arg] = action.payload;
        state.getCabinsLoading = false;
        state.getCabinsError = false;
        state.getCabinsSuccess = true;
        state.getCabinsErrorMessage = null;
      });

    builder
      .addCase(deleteCabinThunk.pending, (state, action) => {
        state.deleteCabinLoading = true;
        state.deleteCabinError = false;
        state.deleteCabinSuccess = false;
        state.deleteCabinErrorMessage = null;
      })
      .addCase(deleteCabinThunk.rejected, (state, action) => {
        state.deleteCabinLoading = false;
        state.deleteCabinError = true;
        state.deleteCabinSuccess = false;
        state.deleteCabinErrorMessage = action.payload?.message;
      })
      .addCase(deleteCabinThunk.fulfilled, (state, action) => {
        state.deleteCabinLoading = false;
        state.deleteCabinError = false;
        state.deleteCabinSuccess = true;
        state.deleteCabinErrorMessage = null;
        state.cabins[action.meta.arg.m_id] = state.cabins[
          action.meta.arg.m_id
        ].filter((cabin) => cabin.id !== action.meta.arg.id);
      });

    builder
      .addCase(createCabinThunk.pending, (state, action) => {
        state.createCabinLoading = true;
        state.createCabinError = false;
        state.createCabinSuccess = false;
        state.createCabinErrorMessage = null;
      })
      .addCase(createCabinThunk.rejected, (state, action) => {
        state.createCabinLoading = false;
        state.createCabinError = true;
        state.createCabinSuccess = false;
        state.createCabinErrorMessage = action.payload?.message;
      })
      .addCase(createCabinThunk.fulfilled, (state, action) => {
        console.log("action", action);
        if (Boolean(state.currentMachine)) {
          state.currentMachine.cabin_count = action.payload.length;
        }
        const machine = state.machines.find(
          (item) => item.id === action.payload[0].machine
        );
        if (machine) {
          machine.cabin_count += action.payload.length;
        }

        state.cabins[action.payload.machine][action.payload.id] =
          action.payload;

        state.createCabinLoading = false;
        state.createCabinError = false;
        state.createCabinSuccess = true;
        state.createCabinErrorMessage = null;
      });

    builder
      .addCase(updateCabinThunk.pending, (state, action) => {
        state.updateCabinLoading = true;
        state.updateCabinError = false;
        state.updateCabinSuccess = false;
        state.updateCabinErrorMessage = null;
      })
      .addCase(updateCabinThunk.rejected, (state, action) => {
        state.updateCabinLoading = false;
        state.updateCabinError = true;
        state.updateCabinSuccess = false;
        state.updateCabinErrorMessage = action.payload?.message;
      })
      .addCase(updateCabinThunk.fulfilled, (state, action) => {
        state.cabins[action.meta.arg.data.machine] = state.cabins[
          action.meta.arg.data.machine
        ].map((cabin) =>
          cabin.id === action.meta.arg.id ? action.payload : cabin
        );
        state.updateCabinLoading = false;
        state.updateCabinError = false;
        state.updateCabinSuccess = true;
        state.updateCabinErrorMessage = null;
      });

    // Trays
    builder
      .addCase(createMultyTrayThunk.pending, (state, action) => {
        state.createMultyTrayLoading[action.meta.arg.cabin] = true;
        state.createMultyTrayError[action.meta.arg.cabin] = false;
        state.createMultyTraySuccess[action.meta.arg.cabin] = false;
        state.createMultyTrayErrorMessage[action.meta.arg.cabin] = null;
      })
      .addCase(createMultyTrayThunk.rejected, (state, action) => {
        state.createMultyTrayLoading[action.meta.arg.cabin] = false;
        state.createMultyTrayError[action.meta.arg.cabin] = true;
        state.createMultyTraySuccess[action.meta.arg.cabin] = false;
        state.createMultyTrayErrorMessage[action.meta.arg.cabin] =
          action.payload?.message;
      })
      .addCase(createMultyTrayThunk.fulfilled, (state, action) => {
        state.createMultyTrayLoading[action.meta.arg.cabin] = false;
        state.createMultyTrayError[action.meta.arg.cabin] = false;
        state.createMultyTraySuccess[action.meta.arg.cabin] = true;
        state.createMultyTrayErrorMessage[action.meta.arg.cabin] = null;

        const machine = state.machines.find(
          (item) => item.id === action.payload[0].machine
        );
        console.log("Machine", machine);
        if (machine) {
          machine.tray_count += action.payload.length;
        }

        if (action.meta.arg.cabin in state.trays) {
          state.trays[action.meta.arg.cabin].push(action.payload);
        } else {
          state.trays[action.meta.arg.cabin] = action.payload;
        }
      });

    builder
      .addCase(createTrayThunk.pending, (state, action) => {
        state.createTrayLoading = true;
        state.createTrayError = false;
        state.createTraySuccess = false;
        state.createTrayErrorMessage = null;
      })
      .addCase(createTrayThunk.rejected, (state, action) => {
        state.createTrayLoading = false;
        state.createTrayError = true;
        state.createTraySuccess = false;
        state.createTrayErrorMessage = action.payload?.message;
      })
      .addCase(createTrayThunk.fulfilled, (state, action) => {
        state.createTrayLoading = false;
        state.createTrayError = false;
        state.createTraySuccess = true;
        state.createTrayErrorMessage = null;

        const machine = state.machines.find(
          (item) => item.id === action.payload[0].machine
        );
        console.log("Machine", machine);
        if (machine) {
          machine.tray_count += 1;
        }

        if (action.meta.arg.cabin in state.trays) {
          state.trays[action.meta.arg.cabin].push(action.payload);
        } else {
          state.trays[action.meta.arg.cabin] = action.payload;
        }
      });

    builder
      .addCase(getTraysByCabinThunk.pending, (state, action) => {
        state.getTrayByCabinLoading = true;
        state.getTrayByCabinError = false;
        state.getTrayByCabinSuccess = false;
        state.getTrayByCabinErrorMessage = null;
      })
      .addCase(getTraysByCabinThunk.rejected, (state, action) => {
        state.getTrayByCabinLoading = false;
        state.getTrayByCabinError = true;
        state.getTrayByCabinSuccess = false;
        state.getTrayByCabinErrorMessage = action.payload?.message;
      })
      .addCase(getTraysByCabinThunk.fulfilled, (state, action) => {
        state.getTrayByCabinLoading = false;
        state.getTrayByCabinError = false;
        state.getTrayByCabinSuccess = true;
        state.getTrayByCabinErrorMessage = null;

        const machine = state.machines.find(
          (item) => item.id === action.payload[0].machine
        );
        console.log("Machine", machine);
        if (machine) {
          machine.tray_count += action.payload.length;
        }

        state.trays[action.meta.arg] = action.payload;
      });

    builder
      .addCase(deleteTrayThunk.pending, (state, action) => {
        state.deleteTrayLoading = true;
        state.deleteTrayError = false;
        state.deleteTraySuccess = false;
        state.deleteTrayErrorMessage = null;
      })
      .addCase(deleteTrayThunk.rejected, (state, action) => {
        state.deleteTrayLoading = false;
        state.deleteTrayError = true;
        state.deleteTraySuccess = false;
        state.deleteTrayErrorMessage = action.payload?.message;
      })
      .addCase(deleteTrayThunk.fulfilled, (state, action) => {
        state.deleteTrayLoading = false;
        state.deleteTrayError = false;
        state.deleteTraySuccess = true;
        state.deleteTrayErrorMessage = null;
        state.trays[action.meta.arg.cabin_id] = state.trays[
          action.meta.arg.cabin_id
        ]?.filter((item) => item.id !== action.meta.arg.id);
      });
    // Partition
    builder
      .addCase(createMultiplePartitionThunk.pending, (state, action) => {
        state.createMultiplePartitionLoading[action.meta.arg.tray] = true;
        state.createMultiplePartitionError[action.meta.arg.tray] = false;
        state.createMultiplePartitionSuccess[action.meta.arg.tray] = false;
        state.createMultiplePartitionErrorMessage[action.meta.arg.tray] = null;
      })
      .addCase(createMultiplePartitionThunk.rejected, (state, action) => {
        state.createMultiplePartitionLoading[action.meta.arg.tray] = false;
        state.createMultiplePartitionError[action.meta.arg.tray] = true;
        state.createMultiplePartitionSuccess[action.meta.arg.tray] = false;
        state.createMultiplePartitionErrorMessage[action.meta.arg.tray] =
          action.payload?.message;
      })
      .addCase(createMultiplePartitionThunk.fulfilled, (state, action) => {
        state.createMultiplePartitionLoading[action.meta.arg.tray] = false;
        state.createMultiplePartitionError[action.meta.arg.tray] = false;
        state.createMultiplePartitionSuccess[action.meta.arg.tray] = true;
        state.createMultiplePartitionErrorMessage[action.meta.arg.tray] = null;

        if (action.meta.arg.tray in state.partitions) {
          state.partitions[action.meta.arg.tray] = [
            ...state.partitions[action.meta.arg.tray],
            ...action.payload,
          ];
        } else {
          state.partitions[action.meta.arg.tray] = action.payload;
        }
      });

    builder
      .addCase(getPartitionsByTrayThunk.pending, (state, action) => {
        state.getPartitionsByTrayLoading = true;
        state.getPartitionsByTrayError = false;
        state.getPartitionsByTraySuccess = false;
        state.getPartitionsByTrayErrorMessage = null;
      })
      .addCase(getPartitionsByTrayThunk.rejected, (state, action) => {
        state.getPartitionsByTrayLoading = false;
        state.getPartitionsByTrayError = true;
        state.getPartitionsByTraySuccess = false;
        state.getPartitionsByTrayErrorMessage = action.payload?.message;
      })
      .addCase(getPartitionsByTrayThunk.fulfilled, (state, action) => {
        state.partitions[action.meta.arg] = action.payload;
        state.getPartitionsByTrayLoading = false;
        state.getPartitionsByTrayError = false;
        state.getPartitionsByTraySuccess = true;
        state.getPartitionsByTrayErrorMessage = null;
      });

    builder
      .addCase(deletePartitionThunk.pending, (state, action) => {
        state.deletePartitionLoading[action.meta.arg.id] = true;
        state.deletePartitionError = {};
        state.deletePartitionSuccess = {};
        state.deletePartitionErrorMessage = {};
      })
      .addCase(deletePartitionThunk.rejected, (state, action) => {
        state.deletePartitionLoading = {};
        state.deletePartitionError[action.meta.arg.id] = true;
        state.deletePartitionSuccess = {};
        state.deletePartitionErrorMessage[action.meta.arg.id] =
          action.payload?.message;
      })
      .addCase(deletePartitionThunk.fulfilled, (state, action) => {
        state.deletePartitionLoading = {};
        state.deletePartitionError = {};
        state.deletePartitionSuccess[action.meta.arg.id] = true;
        state.deletePartitionErrorMessage = {};
        state.partitions[action.meta.arg.tray] = state.partitions[
          action.meta.arg.tray
        ]?.filter((item) => item.id !== action.meta.arg.id);
      });

    builder
      .addCase(createPartitionThunk.pending, (state, action) => {
        state.createPartitionLoading = true;
        state.createPartitionError = false;
        state.createPartitionSuccess = false;
        state.createPartitionErrorMessage = null;
      })
      .addCase(createPartitionThunk.rejected, (state, action) => {
        state.createPartitionLoading = false;
        state.createPartitionError = true;
        state.createPartitionSuccess = false;
        state.createPartitionErrorMessage = action.payload?.message;
      })
      .addCase(createPartitionThunk.fulfilled, (state, action) => {
        state.createPartitionLoading = false;
        state.createPartitionError = false;
        state.createPartitionSuccess = true;
        state.createPartitionErrorMessage = null;

        // const machine = state.trays.find(
        //   (item) => item.id === action.payload[0].machine
        // );
        // console.log("Machine", machine);
        // if (machine) {
        //   machine.tray_count += 1;
        // }

        if (action.meta.arg.tray in state.partitions) {
          state.partitions[action.meta.arg.tray].push(action.payload);
        } else {
          state.partitions[action.meta.arg.tray] = action.payload;
        }
      });

    builder
      .addCase(updatePartitionThunk.pending, (state, action) => {
        state.updatePartitionLoading = true;
        state.updatePartitionError = false;
        state.updatePartitionSuccess = false;
        state.updatePartitionErrorMessage = null;
      })
      .addCase(updatePartitionThunk.rejected, (state, action) => {
        state.updatePartitionLoading = false;
        state.updatePartitionError = true;
        state.updatePartitionSuccess = false;
        state.updatePartitionErrorMessage = action.payload?.message;
      })
      .addCase(updatePartitionThunk.fulfilled, (state, action) => {
        state.updatePartitionLoading = false;
        state.updatePartitionError = false;
        state.updatePartitionSuccess = true;
        state.updatePartitionErrorMessage = null;

        var item = state.partitions[action.payload.tray].findIndex(
          (item) => item.id === action.payload.id
        );

        state.partitions[action.payload.tray][item] = action.payload;
      });

    // Machine photos
    machinePhotoBuilder(builder);
    // Assignments
    machineAssignmentBuilder(builder);
    // Assign to location
    machineAssignmentToLocationBuilder(builder);
  },
});

export const {
  clearMachineState,
  clearMachineUser,
  clearCabinState,
  clearTrayState,
  clearPartitionState,
} = vending.actions;

export default vending.reducer;
